export const dashboardRoutePatterns = [
  "/admin/internal-monitors/[org]/[ruleId]",
  "/[org]/runs/[runId]/jobs/[jobId]",
  "/[org]/codesmith/[repo]/pr/[prNumber]",
  "/[org]/automations/[automationId]/runs",
  "/[org]/automations/[automationId]/edit",
  "/[org]/codesmith/machine-definitions/[definitionId]",
  "/admin/internal-monitors/[org]",
  "/[org]/stickydisks/[repository]",
  "/[org]/sessions/[[...sessionPath]]",
  "/[org]/monitors/[monitorId]",
  "/[org]/machine-definitions/[definitionId]",
  "/[org]/projects/[projectId]",
  "/[org]/runs/workflows",
  "/[org]/runs/jobs",
  "/[org]/runs/[runId]",
  "/[org]/canvas/[uuid]",
  "/[org]/cache/[repository]",
  "/[org]/automations/runs",
  "/admin/internal-monitors",
  "/admin/codesmith-leaderboard",
  "/[org]/settings/codesmith/secrets",
  "/[org]/codesmith/machine-definitions",
  "/[org]/codesmith/connect-slack",
  "/[org]/codesmith/connect-linear",
  "/[org]/codesmith/proof",
  "/[org]/proof/shadow-warm-up",
  "/[org]/codesmith-autofix-configuration",
  "/[org]/settings/codesmith/skills",
  "/[org]/settings/codesmith/review",
  "/[org]/settings/codesmith/mcp",
  "/[org]/settings/integrations",
  "/[org]/github-actions-analytics",
  "/[org]/settings/appearance",
  "/[org]/settings/codesmith",
  "/[org]/settings/features",
  "/[org]/settings/caching",
  "/[org]/settings/proof",
  "/[org]/settings/team",
  "/[org]/network-monitoring",
  "/[org]/machine-definitions",
  "/[org]/mcp-connections",
  "/[org]/docker-analytics",
  "/[org]/automations",
  "/[org]/projects",
  "/[org]/autofix-settings",
  "/[org]/stickydisks",
  "/[org]/testboxes",
  "/[org]/settings",
  "/[org]/support",
  "/[org]/monitors",
  "/[org]/rightsize",
  "/[org]/analytics",
  "/[org]/fusion-lab",
  "/[org]/usage",
  "/[org]/floor",
  "/[org]/home",
  "/[org]/cache",
  "/[org]/runs",
  "/[org]/logs",
  "/debug/sso-reauthorization",
  "/debug/session-inspector",
  "/codesmith-autofix-configuration",
  "/github-actions-analytics",
  "/codesmith/proof",
  // After "/codesmith/proof", which it would otherwise claim as org "codesmith".
  "/[org]/proof",
  "/network-monitoring",
  "/machine-definitions",
  "/enable-autofix",
  "/docker-analytics",
  "/settings/[[...section]]",
  "/sessions/[[...sessionPath]]",
  "/canvas-sandbox",
  "/canvas-capture",
  "/autofix-settings",
  "/copy-prompt",
  "/stickydisks",
  "/rightsize",
  "/testboxes",
  "/extension",
  "/support",
  "/monitors",
  "/analytics",
  "/welcome",
  "/login",
  "/usage",
  "/cache",
  "/home",
  "/proof",
  "/runs",
  "/logs",
  "/cli/auth",
  "/github-app-setup",
  "/admin",
  "/",
  "/[org]",
  "/[...unmatched]",
] as const;

type RouteParam = { name: string; catchAll: boolean };

function compileRoutePattern(pattern: string): {
  regex: RegExp;
  params: RouteParam[];
} {
  const params: RouteParam[] = [];
  if (pattern === "/") return { regex: /^\/$/, params };

  const segments = pattern.split("/").slice(1);
  const source = segments
    .map((segment) => {
      const optionalCatchAll = /^\[\[\.\.\.(.+)\]\]$/.exec(segment);
      if (optionalCatchAll) {
        params.push({ name: optionalCatchAll[1], catchAll: true });
        return "(?:/(.+?))?";
      }
      const catchAll = /^\[\.\.\.(.+)\]$/.exec(segment);
      if (catchAll) {
        params.push({ name: catchAll[1], catchAll: true });
        return "/(.+?)";
      }
      const dynamic = /^\[(.+)\]$/.exec(segment);
      if (dynamic) {
        params.push({ name: dynamic[1], catchAll: false });
        return "/([^/]+)";
      }
      return `/${segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`;
    })
    .join("");
  return { regex: new RegExp(`^${source}/?$`), params };
}

const dashboardRoutes = dashboardRoutePatterns.map((pattern) => ({
  pattern,
  ...compileRoutePattern(pattern),
}));

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

const INSTANCE_BASE_PATH = /^\/enterprise\/([^/]+)(?=\/|$)/;

/**
 * A GitHub Enterprise Server instance prefixes every dashboard URL with its
 * slug: `/enterprise/ghe/acme/runs` → `{ instanceSlug: "ghe", pathname: "/acme/runs" }`.
 * github.com URLs carry no prefix.
 */
export function splitInstanceBasePath(pathname: string): {
  instanceSlug: string | null;
  pathname: string;
} {
  const match = INSTANCE_BASE_PATH.exec(pathname);
  if (!match) return { instanceSlug: null, pathname };
  return {
    instanceSlug: decodeSegment(match[1]),
    pathname: pathname.slice(match[0].length) || "/",
  };
}

export function normalizeDashboardPathname(pathname: string): string {
  const routePathname = splitInstanceBasePath(pathname).pathname;
  return (
    dashboardRoutes.find(({ regex }) => regex.test(routePathname))?.pattern ??
    routePathname
  );
}

// Dynamic params read off the pathname rather than `useParams`: Next syncs
// `usePathname` from `history.pushState` (see the shallow branch of
// `navigate`) but keeps the segment tree, so `useParams` would lag a shallow
// update the way `router.query` never did under the Pages Router.
export function dashboardRouteParams(
  pathname: string,
): Record<string, string | string[]> | null {
  const routePathname = splitInstanceBasePath(pathname).pathname;
  for (const { regex, params } of dashboardRoutes) {
    const match = regex.exec(routePathname);
    if (!match) continue;
    const values: Record<string, string | string[]> = {};
    params.forEach(({ name, catchAll }, index) => {
      const value = match[index + 1];
      if (value === undefined) return;
      values[name] = catchAll
        ? value.split("/").map(decodeSegment)
        : decodeSegment(value);
    });
    return values;
  }
  return null;
}
